import type { TrackEventProperties } from "./useTrackEvent.ts";
import { MixpanelProperty } from "./mixpanelProperty.ts";

const UTM_SESSION_STORAGE_KEY = "sg-protect-utm-params";

function readUtmParamsFromSearch(search: string): TrackEventProperties {
    const params = new URLSearchParams(search);
    const properties: TrackEventProperties = {};

    const source = params.get("utm_source");
    const medium = params.get("utm_medium");
    const campaign = params.get("utm_campaign");
    const term = params.get("utm_term");
    const content = params.get("utm_content");

    if (source) {
        properties[MixpanelProperty.UtmSource] = source;
    }

    if (medium) {
        properties[MixpanelProperty.UtmMedium] = medium;
    }

    if (campaign) {
        properties[MixpanelProperty.UtmCampaign] = campaign;
    }

    if (term) {
        properties[MixpanelProperty.UtmTerm] = term;
    }

    if (content) {
        properties[MixpanelProperty.UtmContent] = content;
    }

    return properties;
}

/**
 * Must run synchronously at app bootstrap, before any router loader has a chance to redirect
 * and drop the query string (e.g. host-app's index route redirect).
 */
export function captureUtmParamsFromUrl(): void {
    if (typeof window === "undefined") {
        return;
    }

    const properties = readUtmParamsFromSearch(window.location.search);

    try {
        if (Object.keys(properties).length === 0) {
            sessionStorage.removeItem(UTM_SESSION_STORAGE_KEY);
        } else {
            sessionStorage.setItem(UTM_SESSION_STORAGE_KEY, JSON.stringify(properties));
        }
    } catch {
        // sessionStorage unavailable (private browsing, quota exceeded)
    }
}

const UTM_KEYS: string[] = [
    MixpanelProperty.UtmSource,
    MixpanelProperty.UtmMedium,
    MixpanelProperty.UtmCampaign,
    MixpanelProperty.UtmTerm,
    MixpanelProperty.UtmContent
];

function isValidUtmProperties(value: unknown): value is TrackEventProperties {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return false;
    }

    return Object.entries(value).every(
        ([key, propertyValue]) => UTM_KEYS.includes(key) && typeof propertyValue === "string"
    );
}

export function getUtmProperties(): TrackEventProperties {
    try {
        const stored = sessionStorage.getItem(UTM_SESSION_STORAGE_KEY);

        if (!stored) {
            return {};
        }

        const parsed: unknown = JSON.parse(stored);

        return isValidUtmProperties(parsed) ? parsed : {};
    } catch {
        return {};
    }
}
